import { useContext, useEffect, useRef, useState } from 'react';
import { A, FocusText, TextButton } from 't-lib-ui/widgets';
import { getKeyHandler, useProgressModal, ModalDialog, ButtonBar } from 't-lib-ui/structure';
import { AppState } from '../context/general';
import { usePreferences } from '../connector/preferences';
import { getLastActiveMs } from '../connector/lastActive';
import type { UserPreferences } from '../schema-copy/api-schema';
import './RssBankruptcyCard.css';

const DAY = 24 * 60 * 60 * 1000;

type EngineLike = {
  getFeeds(): { id: string }[];
  getShowRead(id: string): 'u' | 'a';
  getUnreadCount(id: string): number;
};

/** Total unread across tracked (showRead='u') feeds. */
function trackedUnread(engine: EngineLike): number {
  let x = 0;
  for (const f of engine.getFeeds()) if (engine.getShowRead(f.id) == 'u') x += engine.getUnreadCount(f.id);
  return x;
}

/** The raw backlog + inactivity trigger (no stored state). Once met, we persist the
 * 'question' state so the prompt stays until the user decides. */
function criteriaMet(engine: EngineLike): boolean {
  const x = trackedUnread(engine);
  const since = Date.now() - getLastActiveMs();
  return (since > 5 * DAY && x >= 500) || (x > 1000 && since > DAY);
}

/** Whether the bankruptcy card should show. Also used by HomePage to reword the welcome hero. */
export function bankruptcyConditionsMet(engine: EngineLike, preferences: UserPreferences): boolean {
  const state = preferences.bankruptcyState ?? 'none';
  if (state == 'never') return false;
  if (state == 'in' || state == 'question') return true; // sticky once asked / accepted
  if (state == 'delay' && preferences.bankruptcyDelayUntil && Date.now() < Date.parse(preferences.bankruptcyDelayUntil)) return false;
  return criteriaMet(engine); // 'none' or an expired 'delay'
}

// Once the user explicitly dismisses the card this session, keep it hidden until the next
// full load. Module-level so it applies across both Home and My Feeds.
let sessionDismissed = false;
/** Clear the per-session hide (e.g. after re-enabling from Preferences) so the card can
 * show again this session without a reload. */
export function resetBankruptcyDismissal() { sessionDismissed = false; }
const KEEP_OPTIONS = [20, 10, 5, 3, 1, 0];
const OLDER_OPTIONS = [1, 3, 7, 14];

// A minimal subscribable text store so the progress modal's message can be updated live as
// the per-feed clean-up loop advances (useProgressModal only exposes show/hide).
type ProgressStore = { text: string; set(t: string): void; subscribe(l: () => void): () => void };
function makeProgressStore(): ProgressStore {
  const listeners = new Set<() => void>();
  return {
    text: '',
    set(t) { this.text = t; listeners.forEach(l => l()); },
    subscribe(l) { listeners.add(l); return () => { listeners.delete(l); }; },
  };
}
const ProgressMessage = ({ store }: { store: ProgressStore }) => {
  const [, tick] = useState(0);
  useEffect(() => store.subscribe(() => tick(t => t + 1)), [store]);
  return <>{store.text}</>;
};

interface Counts {
  x: number;        // total unread across tracked (showRead='u') feeds
  big: string[];    // tracked feed ids with >100 unread
  y: number;        // tracked feeds with unread>0
  total: number;    // total feeds
}

/**
 * The RSS Bankruptcy card. Self-contained: decides whether to show based on the unread
 * backlog, time since last activity and the stored bankruptcy state, and offers one-click
 * ways to clear the backlog. Shown on Home (`context='home'`, with a My Feeds link) and on
 * My Feeds (`context='myfeeds'`, with on-page guidance instead).
 */
const RssBankruptcyCard: React.FC<{ context: 'home' | 'myfeeds' }> = ({ context }) => {
  const appState = useContext(AppState);
  const engine = appState.feedEngine;
  const [preferences, updatePreferences] = usePreferences();
  const [presentProgress, dismissProgress] = useProgressModal();
  const progressStore = useRef(makeProgressStore()).current;

  const compute = (): Counts => {
    const fs = engine.getFeeds();
    let x = 0, y = 0;
    const big: string[] = [];
    for (const f of fs) {
      const u = engine.getUnreadCount(f.id);
      if (engine.getShowRead(f.id) == 'u') {
        if (u > 100) big.push(f.id);
        if (u > 0) { x += u; y++; }
      }
    }
    return { x, big, y, total: fs.length };
  };

  const [counts, setCounts] = useState<Counts>(compute);
  const [busy, setBusy] = useState(false);
  const [olderDays, setOlderDays] = useState(7);
  const [keepB, setKeepB] = useState(10);
  const [keepC, setKeepC] = useState(10);
  const [confirmNuke, setConfirmNuke] = useState(false);

  useEffect(() => {
    const recompute = () => setCounts(compute());
    const unF = engine.onFeedsChange(recompute);
    const unU = engine.onUnreadChange(recompute);
    recompute();
    return () => { unF(); unU(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine]);

  const refresh = () => setCounts(compute());

  // Promote 'none' (or an expired snooze) into the sticky 'question' state once the criteria
  // are met, and automatically end an unanswered 'question' once the backlog drops below 500.
  const prefState = preferences.bankruptcyState ?? 'none';
  useEffect(() => {
    const delayActive = prefState == 'delay' && !!preferences.bankruptcyDelayUntil && Date.now() < Date.parse(preferences.bankruptcyDelayUntil);
    if ((prefState == 'none' || (prefState == 'delay' && !delayActive)) && criteriaMet(engine))
      updatePreferences({ bankruptcyState: 'question' });
    else if (prefState == 'question' && counts.x < 500)
      updatePreferences({ bankruptcyState: 'none' });
    else if (prefState == 'in' && counts.x < 50)
      updatePreferences({ bankruptcyState: 'none' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefState, counts.x]);

  // Decide visibility.
  if (sessionDismissed) return null;
  if (!bankruptcyConditionsMet(engine, preferences)) return null;

  const { x, big, y } = counts;
  const accepted = (preferences.bankruptcyState ?? 'none') == 'in';
  const variant = !accepted ? 'declare' : x > 100 ? 'in' : 'over';

  // --- State transitions ---
  const accept = () => updatePreferences({ bankruptcyState: 'in' });
  const snooze = () => { sessionDismissed = true; updatePreferences({ bankruptcyState: 'delay', bankruptcyDelayUntil: new Date(Date.now() + 7 * DAY).toISOString() }); };
  const dismiss = () => { sessionDismissed = true; updatePreferences({ bankruptcyState: 'none' }); };
  const never = () => { sessionDismissed = true; updatePreferences({ bankruptcyState: 'never' }); };

  const trackedUnreadIds = () =>
    engine.getFeeds().filter(f => engine.getShowRead(f.id) == 'u' && engine.getUnreadCount(f.id) > 0).map(f => f.id);

  // --- Actions (each marks the user as having accepted bankruptcy) ---
  const doConvert = () => {
    for (const id of big) engine.setShowRead(id, 'a');
    accept();
    refresh();
  };
  // Bulk clean-up over every tracked feed (one CDN round-trip per feed) — can take a while,
  // so show a progress modal while we work and report what was cleared at the end.
  const runCleanup = async (days: number, keep: number, prefs: Partial<UserPreferences>) => {
    if (busy) return;
    setBusy(true);
    updatePreferences({ ...prefs, bankruptcyState: 'in' });
    const ids = trackedUnreadIds();
    const before = trackedUnread(engine);
    progressStore.set(`Cleaning up feed 1 of ${ids.length}…`);
    presentProgress({ message: <ProgressMessage store={progressStore} />, successMessage: '', timeout: 120000, successDuration: 1200 });
    for (let i = 0; i < ids.length; i++) {
      progressStore.set(`Cleaning up feed ${i + 1} of ${ids.length}…`);
      await engine.markOlderThanKeepRead(ids[i], days, keep);
    }
    const cleared = before - trackedUnread(engine);
    dismissProgress(true, undefined, cleared > 0 ? `Marked ${cleared.toLocaleString()} ${cleared == 1 ? 'post' : 'posts'} as read` : 'Nothing left to clear');
    setBusy(false);
    refresh();
  };
  const doOlderKeep = () => runCleanup(olderDays, keepB, { markReadOlderDays: olderDays, markReadKeepCount: keepB });
  const doKeepOnly = () => runCleanup(0, keepC, { markReadKeepCount: keepC });
  const doMarkAll = () => {
    setConfirmNuke(false);
    for (const f of engine.getFeeds()) engine.markAllRead(f.id);
    accept();
    refresh();
  };

  const showA = big.length > 0;
  const showB = y > 0;
  const showC = variant == 'declare' && !showA && !showB;
  const showOptions = variant == 'declare' || variant == 'in';
  const showD = showOptions; // always offer the "go nuclear" option whenever options are shown

  // Selects join the FlowForm order like the buttons: arrows move focus, Space opens the
  // native dropdown to change the value (Enter/Space fall through to the browser).
  const selectKeys = getKeyHandler({ sidewaysBehaviour: 'both', enterBehaviour: 'ignore', spaceBehaviour: 'ignore' });

  const keepSelect = (value: number, onChange: (n: number) => void, sideway = false) => (
    <select className={`rbkSelect flowFocussable ${sideway ? 'sidewayFocus' : ''}`} tabIndex={0} value={value} onKeyDown={selectKeys} onChange={e => onChange(Number(e.target.value))}>
      {KEEP_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
    </select>
  );

  const rbkBtn = (label: string, onClick: () => void, opts: { primary?: boolean; disabled?: boolean; sideway?: boolean } = {}) => {
    const act = () => { if (!opts.disabled) onClick(); };
    return (
      <button
        type="button"
        className={`rbkBtn ${opts.primary ? 'primary' : ''} ${opts.disabled ? '' : 'flowFocussable'} ${opts.sideway ? 'sidewayFocus' : ''}`}
        tabIndex={0}
        disabled={opts.disabled}
        onClick={act}
        onKeyDown={getKeyHandler(act)}
      >{label}</button>
    );
  };

  const headline =
    variant == 'declare' ? 'Declare RSS Bankruptcy?'
    : variant == 'in' ? 'In RSS Bankruptcy'
    : 'RSS Bankruptcy over?';

  // Playful motif: a striped red-and-white life-ring (we'll rescue you from drowning in unread posts).
  const icon = (
    <svg viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="17" stroke="#e23b3b" strokeWidth="10" />
      <circle cx="24" cy="24" r="17" stroke="#fff" strokeWidth="10" strokeDasharray="11 15.7" transform="rotate(45 24 24)" />
      <circle cx="24" cy="24" r="12" fill="none" stroke="#fff" strokeWidth="1.5" />
      <circle cx="24" cy="24" r="29" fill="none" />
    </svg>
  );

  return (
    <div className={`rbkCard ${context == 'myfeeds' ? 'rbkCardMyfeeds' : ''}`}>
      <div className="rbkHead">
        <span className="rbkIcon rbkIconRing">{icon}</span>
        <h2 className="rbkTitle">{headline}</h2>
      </div>

      {variant == 'declare' && (
        <p className="rbkIntro">We see you have <strong>{x.toLocaleString()}</strong> tracked unread posts. That's a lot! Most people won't be able to catch up.<br/>Here some options to get you back on your feet:</p>
      )}
      {variant == 'in' && (
        <p className="rbkIntro">You still have <strong>{x.toLocaleString()}</strong> tracked unread posts. Do you need additional help?</p>
      )}
      {variant == 'over' && (
        <p className="rbkIntro">You have only <strong>{x.toLocaleString()}</strong> tracked unread posts left. Are we good now?</p>
      )}

      {showOptions && (
        <ul className="rbkOptions">
          {showA && (
            <li className="rbkOption">
              <span><strong>{big.length}</strong> {big.length == 1 ? 'feed has' : 'feeds have'} over 100 unread posts. We can <strong>convert them to feeds without unread tracking</strong>.</span>
              {rbkBtn('Convert to untracked', doConvert, { disabled: busy })}
            </li>
          )}
          {showB && (
            <li className="rbkOption">
              <span>You have <strong>{y}</strong> {y == 1 ? 'feed' : 'feeds'} with unread posts. We can <strong>mark them as read and only keep a few</strong>.</span>
              <div className="rbkControls">
                <span>Mark posts older than</span>
                <span className="rbkUnit">
                  <select className="rbkSelect flowFocussable" tabIndex={0} value={olderDays} onKeyDown={selectKeys} onChange={e => setOlderDays(Number(e.target.value))}>
                    {OLDER_OPTIONS.map(d => <option key={d} value={d}>{d} {d == 1 ? 'day' : 'days'}</option>)}
                  </select>,
                </span>
                <span>but keep</span>
                {keepSelect(keepB, setKeepB, true)}
                <span>posts per feed:</span>
                {rbkBtn('Mark them as read', doOlderKeep, { disabled: busy, sideway: true })}
              </div>
            </li>
          )}
          {showC && (
            <li className="rbkOption">
              <div className="rbkControls">
                <span>Mark posts as read, keep only</span>
                {keepSelect(keepC, setKeepC)}
                <span>per feed.</span>
                {rbkBtn('Mark as read', doKeepOnly, { disabled: busy })}
              </div>
            </li>
          )}
          {showD && (
            <li className="rbkOption">
              <span>Go nuclear! Mark <strong>everything</strong> as read.</span>
              {rbkBtn('Mark ALL as read', () => setConfirmNuke(true), { disabled: busy })}
            </li>
          )}
          {context == 'home'
            ? <li className="rbkNote">Need more options? Go to <A className="rbkLink" href="/a/myfeeds" internal>My Feeds</A>.</li>
            : <li className="rbkNote">Otherwise, please use the options on this page to mark feeds as read and disable unread tracking for those feeds where you don't need it.</li>}
        </ul>
      )}

      <div className="rbkButtons">
        {variant == 'declare'
          ? rbkBtn("Ask me later", snooze)
          : rbkBtn("I'm good now", dismiss, { primary: true })}
        <FocusText className="rbkNever" focussable="flow" underline={true} onClick={never}>Never show this again</FocusText>
      </div>

      {confirmNuke && (
        <ModalDialog show title="Mark everything as read" closeButton="close" onClose={() => setConfirmNuke(false)}>
          <p>This marks <strong>every post in all your feeds</strong> as read. This cannot be undone. Are you sure?</p>
          <ButtonBar className="full-margin-top" align="right">
            <TextButton type="minor" onClick={() => setConfirmNuke(false)} label="Cancel" />
            <TextButton type="major" focussable="sideway" onClick={doMarkAll} label="Mark ALL as read" />
          </ButtonBar>
        </ModalDialog>
      )}
    </div>
  );
};

export default RssBankruptcyCard;
